import { useCallback, useEffect, useRef, useState } from "react";

const NEAR_BOTTOM_THRESHOLD_PX = 120;

export const useConversationAutoScroll = ({
  investigationId,
  newestMessageCount,
  progressEventCount,
  newestMessageId,
  newestAgentMessageId,
}: {
  investigationId: string | undefined;
  newestMessageCount: number;
  progressEventCount: number;
  newestMessageId: string | null;
  newestAgentMessageId: string | null;
}) => {
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null
  );
  const isReaderNearBottomRef = useRef(true);
  const previouslySeenAgentIdRef = useRef<string | null>(null);
  const hasObservedFirstNonEmptyThreadRef = useRef(false);
  const pendingScrollToBottomOnNavigationRef = useRef(false);
  const previousInvestigationIdRef = useRef<string | undefined>(undefined);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const conversationScrollRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainer(node);
  }, []);

  useEffect(() => {
    if (!scrollContainer) {
      return;
    }

    const updateNearBottom = () => {
      const distanceFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      const isNearBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
      isReaderNearBottomRef.current = isNearBottom;
      if (isNearBottom) {
        setHasNewMessage(false);
      }
    };

    scrollContainer.addEventListener("scroll", updateNearBottom, {
      passive: true,
    });
    updateNearBottom();

    return () => {
      scrollContainer.removeEventListener("scroll", updateNearBottom);
    };
  }, [scrollContainer]);

  const isThreadEmpty = newestMessageCount === 0 && progressEventCount === 0;

  useEffect(() => {
    if (investigationId === undefined) {
      return;
    }

    if (previousInvestigationIdRef.current === investigationId) {
      return;
    }

    const isInitialMount = previousInvestigationIdRef.current === undefined;
    previousInvestigationIdRef.current = investigationId;

    if (isInitialMount) {
      return;
    }

    pendingScrollToBottomOnNavigationRef.current = true;
    isReaderNearBottomRef.current = true;
    hasObservedFirstNonEmptyThreadRef.current = false;
    previouslySeenAgentIdRef.current = null;
    setHasNewMessage(false);
  }, [investigationId]);

  useEffect(() => {
    if (isThreadEmpty) {
      previouslySeenAgentIdRef.current = newestAgentMessageId;
      return;
    }

    if (pendingScrollToBottomOnNavigationRef.current) {
      pendingScrollToBottomOnNavigationRef.current = false;
      hasObservedFirstNonEmptyThreadRef.current = true;
      previouslySeenAgentIdRef.current = newestAgentMessageId;
      isReaderNearBottomRef.current = true;
      scrollAnchorRef.current?.scrollIntoView({ block: "end" });
      setHasNewMessage(false);
      return;
    }

    const wasInitialized = hasObservedFirstNonEmptyThreadRef.current;
    hasObservedFirstNonEmptyThreadRef.current = true;

    const isNewAgentMessageArrival =
      wasInitialized &&
      newestAgentMessageId !== null &&
      newestAgentMessageId !== previouslySeenAgentIdRef.current;

    previouslySeenAgentIdRef.current = newestAgentMessageId;

    if (!wasInitialized) {
      return;
    }

    if (isReaderNearBottomRef.current) {
      scrollAnchorRef.current?.scrollIntoView({ block: "end" });
    } else if (isNewAgentMessageArrival) {
      setHasNewMessage(true);
    }
  }, [
    newestMessageCount,
    progressEventCount,
    newestMessageId,
    isThreadEmpty,
    newestAgentMessageId,
  ]);

  const jumpToLatest = useCallback(() => {
    scrollAnchorRef.current?.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });
    setHasNewMessage(false);
  }, []);

  return {
    scrollAnchorRef,
    conversationScrollRef,
    hasNewMessage,
    jumpToLatest,
  };
};
